// @pages/document/Prompt.page.tsx
import React, { useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { renderRoute, routeReviewWithDocument } from "@navigation/typedRoutes";
import type {
    AnswerSectionQuestionsRequest,
    ContentError,
    Markdown,
    Section,
} from '@ps-bridge/models/core/dataContracts';
import type { DocumentID } from "@ps-bridge/models/brands/documentID";
import { useContentClient } from '@contexts/ContentClient.context';
import { useDocumentContext } from '@contexts/Document.context';
import { useLoadingController } from '@contexts/Loading.context';

import { ok, type Ok } from "@ps-bridge/ok";
import { mkNavigate, mkRouteBuilder, mkTaskRunner } from "@ps-bridge/capabilities";
import type { AnswerSectionQuestionsFetch } from "@ps-bridge/contracts/documentPrompt";
import * as Option from "@utils/Option";
import { makeMarkdown } from "@ps-bridge/models/brands/markdown";
import { getParamOption } from "@utils/searchParams";
import type { TaskTs } from "@ps-bridge/interop";
import type { StateUpdate } from "@ps-bridge/capabilitiesTypes";
import { ioEffect, taskEitherFromEffect } from '@utils/effects/branded';
import type { DocumentPromptKernel } from "@ps-bridge/contracts/documentPrompt";
import { buildDocumentPromptViewProps } from "@ps-bridge/documentPrompt.program";
import { useRunTaskWithError } from "@hooks/effects/useTaskEffect";
import PromptPagePanel, {
    type PromptPagePhase,
} from "@view/panels/document/PromptPage.panel";

const PromptPage: React.FC = () => {
    const navigate = useNavigate();
    const contentClient = useContentClient();
    const { document, activeSection, controller } = useDocumentContext();
    const [searchParams] = useSearchParams();
    const documentOption = document.read;
    const probingQuestionValue = useMemo(
        () => getParamOption(searchParams, "question"),
        [searchParams],
    );
    const probingQuestion: Markdown = useMemo(
        () => Option.fold(probingQuestionValue, () => makeMarkdown(""), (value) => makeMarkdown(value)),
        [probingQuestionValue],
    );
    const loading = useLoadingController();
    const runTaskWithError = useRunTaskWithError();
    const runTaskWithErrorIo = useCallback(
        (task: TaskTs<Ok>) =>
            ioEffect(() => {
                runTaskWithError(task).then(() => ok);
                return ok;
            }),
        [runTaskWithError],
    );
    const taskRunner = useMemo(() => mkTaskRunner(runTaskWithErrorIo), [runTaskWithErrorIo]);
    const answerSectionQuestionsFetch: AnswerSectionQuestionsFetch = useMemo(() => {
        const invoke = (documentId: DocumentID, request: AnswerSectionQuestionsRequest) =>
            taskEitherFromEffect(
                contentClient.answerSectionQuestions(documentId, request),
                (reason) => reason as ContentError,
            );

        function fetch(documentId: DocumentID, request: AnswerSectionQuestionsRequest): ReturnType<typeof invoke>;
        function fetch(documentId: DocumentID): (request: AnswerSectionQuestionsRequest) => ReturnType<typeof invoke>;
        function fetch(documentId: DocumentID, request?: AnswerSectionQuestionsRequest) {
            if (request === undefined) {
                return (nextRequest: AnswerSectionQuestionsRequest) => invoke(documentId, nextRequest);
            }
            return invoke(documentId, request);
        }

        return Object.assign(fetch, {
            __brandAnswerSectionQuestionsFetch: "AnswerSectionQuestionsFetch" as const,
        }) as AnswerSectionQuestionsFetch;
    }, [contentClient]);
    const kernel: DocumentPromptKernel = useMemo(
        () => ({
            loading,
            navigate: mkNavigate((route) =>
                ioEffect(() => {
                    navigate(renderRoute(route), { replace: true });
                    return ok;
                }),
            ),
            reviewRoute: mkRouteBuilder(routeReviewWithDocument),
            document,
            activeSection,
            updateSection: controller.updateSection as StateUpdate<Section>,
            answerSectionQuestions: answerSectionQuestionsFetch,
            taskRunner,
        }),
        [
            activeSection,
            answerSectionQuestionsFetch,
            controller.updateSection,
            document,
            navigate,
            loading,
            taskRunner,
        ],
    );
    const viewProps = useMemo(
        () =>
            buildDocumentPromptViewProps({
                kernel,
                probingQuestion,
            }),
        [kernel, probingQuestion],
    );

    const phase: PromptPagePhase = Option.fold(
        documentOption,
        () => "missing",
        () => "ready",
    );

    return <PromptPagePanel phase={phase} viewProps={viewProps} />;
};

export default PromptPage;
